// Sub-tour step definitions for EQUIPR CORE
// Each sub-tour guides users through a specific workflow

export const SUB_TOURS = {
  adding_customers: {
    id: 'adding_customers',
    title: 'Adding Customers',
    description: 'Add your first customer manually or import from your accounting software.',
    icon: 'Building2',
    estimatedTime: '3 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Adding Customers',
        content: 'Let\'s add your first customer. You can add customers manually or import them from your accounting software (Xero or MYOB).',
        choices: [
          { id: 'manual', label: 'Add Manually', icon: 'Plus' },
          { id: 'import', label: 'Import via Xero/MYOB', icon: 'Download' }
        ]
      },
      // Manual path
      {
        id: 'navigate_customers',
        type: 'action',
        path: '/customers',
        title: 'Navigate to Customers',
        content: 'Click on Customers in the menu to go to the Customers page.',
        target: '[href="/customers"]',
        clickTarget: true,
        forChoice: 'manual'
      },
      {
        id: 'click_add',
        type: 'action',
        title: 'Add a Customer',
        content: 'Click the "Add Customer" button to create a new customer.',
        target: '[data-testid="add-customer-btn"]',
        clickTarget: true,
        forChoice: 'manual'
      },
      {
        id: 'fill_form',
        type: 'form',
        title: 'Enter Customer Details',
        content: 'Fill in your customer\'s details. Company name is required. You can add contact information and job sites.',
        target: '[data-testid="customer-form"]',
        pauseForInput: true,
        forChoice: 'manual'
      },
      {
        id: 'save_customer',
        type: 'action',
        title: 'Save Customer',
        content: 'Once you\'ve entered the details, click Save to create the customer.',
        target: '[data-testid="save-customer-btn"]',
        clickTarget: true,
        forChoice: 'manual'
      },
      // Import path
      {
        id: 'navigate_settings',
        type: 'action',
        path: '/settings',
        title: 'Navigate to Settings',
        content: 'Click on Settings to access your accounting integration.',
        target: '[data-testid="settings-nav-btn"]',
        clickTarget: true,
        forChoice: 'import'
      },
      {
        id: 'go_accounting',
        type: 'action',
        title: 'Accounting Integration',
        content: 'Click on the Accounting tab to set up Xero or MYOB integration.',
        target: '[data-testid="tab-accounting"]',
        clickTarget: true,
        forChoice: 'import'
      },
      {
        id: 'setup_integration',
        type: 'form',
        title: 'Connect Your Accounting Software',
        content: 'Follow the prompts to connect your Xero or MYOB account. Once connected, you can import your customers automatically.',
        target: '[data-testid="accounting-integration"]',
        pauseForInput: true,
        forChoice: 'import'
      },
      // Completion
      {
        id: 'complete',
        type: 'complete',
        title: 'Customers Ready!',
        content: 'Great work! You\'ve set up your customers. Next, let\'s add your equipment.',
        nextTour: 'adding_equipment'
      }
    ]
  },

  adding_equipment: {
    id: 'adding_equipment',
    title: 'Adding Assets',
    description: 'Add your assets, vehicles, and attachments.',
    icon: 'Truck',
    estimatedTime: '3 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Adding Assets',
        content: 'Now let\'s add your assets. This includes cranes, trucks, excavators, or any equipment you hire out.'
      },
      {
        id: 'navigate_equipment',
        type: 'action',
        path: '/equipment',
        title: 'Navigate to Assets',
        content: 'Click on Assets in the menu.',
        target: '[href="/equipment"]',
        clickTarget: true
      },
      {
        id: 'click_add',
        type: 'action',
        title: 'Add Asset',
        content: 'Click "Add Asset" to register a new asset.',
        target: '[data-testid="add-equipment-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_form',
        type: 'form',
        title: 'Enter Asset Details',
        content: 'Enter your asset details including name, registration, and type. You can also add compliance documents like rego and insurance.',
        target: '[data-testid="equipment-form"]',
        pauseForInput: true
      },
      {
        id: 'save_equipment',
        type: 'action',
        title: 'Save Asset',
        content: 'Click Save to add this asset to your fleet.',
        target: '[data-testid="equipment-submit-btn"]',
        clickTarget: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Asset Added!',
        content: 'Your asset is now registered. Next, let\'s add your operators who will use this asset.',
        nextTour: 'adding_operators'
      }
    ]
  },

  adding_operators: {
    id: 'adding_operators',
    title: 'Adding Operators',
    description: 'Add your field operators who will submit dockets.',
    icon: 'Users',
    estimatedTime: '3 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Adding Operators',
        content: 'Operators are your field workers who will submit dockets via the mobile app. Let\'s add your first operator.'
      },
      {
        id: 'navigate_operators',
        type: 'action',
        path: '/operators',
        title: 'Navigate to Operators',
        content: 'Click on Operators in the menu.',
        target: '[href="/operators"]',
        clickTarget: true
      },
      {
        id: 'click_add',
        type: 'action',
        title: 'Add Operator',
        content: 'Click "Add Operator" to create a new operator account.',
        target: '[data-testid="add-operator-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_form',
        type: 'form',
        title: 'Enter Operator Details',
        content: 'Enter the operator\'s name, email, and phone. They\'ll receive login credentials to access the mobile portal.',
        target: '[data-testid="operator-form"]',
        pauseForInput: true
      },
      {
        id: 'save_operator',
        type: 'action',
        title: 'Save Operator',
        content: 'Click Save to create the operator account.',
        target: '[data-testid="operator-submit-btn"]',
        clickTarget: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Operator Added!',
        content: 'Your operator can now log in to the mobile portal. Next, let\'s create your first job.',
        nextTour: 'creating_job'
      }
    ]
  },

  creating_job: {
    id: 'creating_job',
    title: 'Creating a Job',
    description: 'Create a job and allocate equipment and operators.',
    icon: 'Calendar',
    estimatedTime: '4 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Creating a Job',
        content: 'Jobs are scheduled work for your customers. Let\'s create a job and allocate your equipment and operators.'
      },
      {
        id: 'navigate_schedule',
        type: 'action',
        path: '/schedule',
        title: 'Navigate to Schedule',
        content: 'Click on Schedule in the menu.',
        target: '[href="/schedule"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'click_add',
        type: 'action',
        title: 'Create New Job',
        content: 'Click "New Job" to create a job.',
        target: '[data-testid="new-job-btn"]',
        clickTarget: true
      },
      {
        id: 'select_customer',
        type: 'form',
        title: 'Select Customer',
        content: 'Choose the customer this job is for. Select one of the customers you added earlier.',
        target: '[data-testid="job-customer-select"]',
        pauseForInput: true
      },
      {
        id: 'job_details',
        type: 'form',
        title: 'Job Details',
        content: 'Enter the job details including date, site, and any special instructions.',
        target: '[data-testid="job-form"]',
        pauseForInput: true
      },
      {
        id: 'save_job',
        type: 'action',
        title: 'Save Job',
        content: 'Click Save to create the job. After creating, you can allocate equipment and operators from the schedule view.',
        target: '[data-testid="save-job-btn"]',
        clickTarget: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Job Created!',
        content: 'Your job is scheduled and allocated. Before processing dockets, let\'s set up your pricelist.',
        nextTour: 'setting_up_pricelist'
      }
    ]
  },

  setting_up_pricelist: {
    id: 'setting_up_pricelist',
    title: 'Setting Up Pricelist',
    description: 'Configure your services and rates for invoicing.',
    icon: 'DollarSign',
    estimatedTime: '3 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Setting Up Your Pricelist',
        content: 'Your pricelist defines what you charge for services. We\'ll create a price list, then add asset and worker rates so dockets are valued correctly.'
      },
      {
        id: 'navigate_data',
        type: 'action',
        path: '/data-management',
        title: 'Navigate to Data Management',
        content: 'Click on Data Management in the menu.',
        target: '[href="/data-management"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'services_tab',
        type: 'action',
        title: 'Pricing & Billing',
        content: 'Click on the Pricing & Billing tab to access your price lists and rates.',
        target: '[data-testid="parent-tab-pricing"]',
        clickTarget: true
      },
      {
        id: 'add_service',
        type: 'action',
        title: 'Add Price List',
        content: 'Click "Add Price List" to create a new price list.',
        target: '[data-testid="add-price-list-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_service',
        type: 'form',
        title: 'Price List Details',
        content: 'Enter a name for your price list (e.g., "Standard Rates"). You can add a description and toggle "Default" if this should be the default for new customers.',
        target: '[data-testid="service-form"]',
        pauseForInput: true
      },
      {
        id: 'save_service',
        type: 'action',
        title: 'Create Price List',
        content: 'Click "Create" to save your new price list.',
        target: '[data-testid="save-service-btn"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'open_price_list',
        type: 'action',
        title: 'Open Your Price List',
        content: 'Click on your new price list to open it and add rates.',
        target: '[data-testid^="price-list-row-"]',
        clickTarget: true
      },
      {
        id: 'add_asset_rate',
        type: 'action',
        title: 'Add an Asset Rate',
        content: 'Click "Add Item" to set a rate for one of your equipment types.',
        target: '[data-testid="add-asset-rate-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_asset_rate',
        type: 'form',
        title: 'Asset Rate Details',
        content: 'Select an asset type, set the base rate, choose the unit of measure (e.g., HOUR or DAY), and set a minimum hire.',
        target: '[data-testid="asset-rate-form"]',
        pauseForInput: true
      },
      {
        id: 'save_asset_rate',
        type: 'action',
        title: 'Save Asset Rate',
        content: 'Click "Add Item" to save this rate.',
        target: '[data-testid="save-asset-rate-btn"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'add_worker_rate',
        type: 'action',
        title: 'Add a Worker Rate',
        content: 'Now add a worker rate. Click "Add Rate" to set pricing for a worker type.',
        target: '[data-testid="add-worker-rate-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_worker_rate',
        type: 'form',
        title: 'Worker Rate Details',
        content: 'Select a worker type (e.g., "Operator"), set the hourly rate, and choose a unit of measure.',
        target: '[data-testid="worker-rate-form"]',
        pauseForInput: true
      },
      {
        id: 'save_worker_rate',
        type: 'action',
        title: 'Save Worker Rate',
        content: 'Click "Add Rate" to save this worker rate.',
        target: '[data-testid="save-worker-rate-btn"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Pricelist Ready!',
        content: 'Your pricelist now has asset and worker rates. When operators submit dockets, they\'ll be valued correctly. You can always add more rates later. Let\'s learn how to process dockets.',
        nextTour: 'processing_docket'
      }
    ]
  },

  processing_docket: {
    id: 'processing_docket',
    title: 'Processing a Docket',
    description: 'Review submitted dockets and prepare for invoicing.',
    icon: 'FileText',
    estimatedTime: '3 min',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Processing Dockets',
        content: 'When operators complete work, they submit dockets. You\'ll review these dockets, make any adjustments, and approve them for invoicing.'
      },
      {
        id: 'navigate_dockets',
        type: 'action',
        path: '/dockets',
        title: 'Navigate to Dockets',
        content: 'Click on Dockets in the menu.',
        target: '[href="/dockets"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'select_docket',
        type: 'action',
        title: 'Select a Docket',
        content: 'Click on a pending docket to review it. You\'ll see the hours worked, equipment used, and any materials.',
        target: '[data-testid="docket-row"]',
        clickTarget: true
      },
      {
        id: 'review_details',
        type: 'info',
        title: 'Review Docket Details',
        content: 'Check the recorded hours, equipment, and any notes from the operator. You can make adjustments if needed.'
      },
      {
        id: 'approve_docket',
        type: 'action',
        title: 'Approve Docket',
        content: 'Once you\'re satisfied, click "Approve" to mark this docket ready for invoicing.',
        target: '[data-testid="approve-docket-btn"]',
        clickTarget: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Docket Processed!',
        content: 'The docket is now approved and ready for invoicing. You\'ve completed the core workflow!'
      }
    ]
  },

  configuring_materials: {
    id: 'configuring_materials',
    title: 'Configuring Materials',
    description: 'Set up material types and tip locations for earthmoving.',
    icon: 'Mountain',
    estimatedTime: '4 min',
    requiresIndustry: 'earthmoving',
    steps: [
      {
        id: 'intro',
        type: 'info',
        title: 'Configuring Materials',
        content: 'For earthmoving work, you can track material pickups and disposals. Let\'s set up your material types and locations.'
      },
      {
        id: 'navigate_data',
        type: 'action',
        path: '/data-management',
        title: 'Navigate to Data Management',
        content: 'Click on Data Management in the menu.',
        target: '[href="/data-management"]',
        placement: 'right',
        clickTarget: true
      },
      {
        id: 'materials_tab',
        type: 'action',
        title: 'Materials Tab',
        content: 'Click on the Materials tab to manage material types.',
        target: '[data-testid="tab-material-types"]',
        clickTarget: true
      },
      {
        id: 'add_material',
        type: 'action',
        title: 'Add Material Type',
        content: 'Click "Add Material" to create material types like Sand, Gravel, Clean Fill, etc.',
        target: '[data-testid="add-material-type-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_material',
        type: 'form',
        title: 'Material Details',
        content: 'Enter the material name and default unit of measurement (tonnes, cubic metres, or loads).',
        target: '[data-testid="material-form"]',
        pauseForInput: true
      },
      {
        id: 'navigate_tips',
        type: 'action',
        path: '/tips-materials',
        title: 'Tips & Materials',
        content: 'Click on Tips & Materials in the menu.',
        target: '[href="/tips-materials"]',
        clickTarget: true
      },
      {
        id: 'add_location',
        type: 'action',
        title: 'Add Location',
        content: 'Click "Add Location" to register a tip, quarry, or material yard.',
        target: '[data-testid="add-location-btn"]',
        clickTarget: true
      },
      {
        id: 'fill_location',
        type: 'form',
        title: 'Location Details',
        content: 'Enter the location details and set up pricing for each material type available there.',
        target: '[data-testid="location-form"]',
        pauseForInput: true
      },
      {
        id: 'complete',
        type: 'complete',
        title: 'Materials Configured!',
        content: 'Your materials and locations are set up. Operators can now record material loads on their dockets.'
      }
    ]
  }
};

// Get list of sub-tours available for user based on role and industry
export const getAvailableSubTours = (isAdmin, industry) => {
  return Object.values(SUB_TOURS).filter(tour => {
    if (tour.requiresAdmin && !isAdmin) return false;
    if (tour.requiresIndustry && tour.requiresIndustry !== industry) return false;
    return true;
  });
};

// Get ordered list of sub-tours for the training section
export const getOrderedSubTours = (isAdmin, industry) => {
  const order = [
    'adding_customers',
    'adding_equipment', 
    'adding_operators',
    'creating_job',
    'setting_up_pricelist',
    'processing_docket',
    'configuring_materials'
  ];
  
  const available = getAvailableSubTours(isAdmin, industry);
  return order
    .map(id => available.find(t => t.id === id))
    .filter(Boolean);
};
